import type { EsSortDirection, EsSortOrder } from '#graphql-operations'

export function useCatalogPaginator() {
  interface SortOption {
    key: string
    field: EsSortOrder
    direction: EsSortDirection
    label: string
  }

  // Paginator data &  methods
  const view = useLocalStorage<'grid' | 'list'>('viewType', 'grid', { initOnMounted: true })
  function updateView(value: 'grid' | 'list') {
    view.value = value
  }

  const defaultPage = 1
  function updatePage(value: number) {
    const route = useRoute()
    const router = useRouter()

    const query = {
      ...route.query,
      p: value,
    }

    if (value === defaultPage) {
      delete query.p
    }

    router.push({ query })
  }

  const totalCount = useState<number>('totalCount', () => 0)
  const itemsPerPageOptions: number[] = [24, 36, 48, 60, 72]
  const defaultItemsPerPage = itemsPerPageOptions[0]
  function updateItemsPerPage(value: number) {
    const route = useRoute()
    const router = useRouter()

    const query = {
      ...route.query,
      limit: value,
    }

    if (value === defaultItemsPerPage) {
      delete query.limit
    }

    delete query.p // Reset page

    router.push({ query })
  }

  const sortOptions: SortOption[] = [
    { key: 'popular', field: 'DEFAULT', direction: 'DESC', label: 'Most Popular' }, // defaultSort
    // { key: 'popular', field: 'POPULAR', direction: 'DESC' },
    // { key: 'season', field: 'SEASON', direction: 'DESC' },
    // { key: 'relevance', field: 'RELEVANCE', direction: 'DESC' },
    // { key: 'discount', field: 'DISCOUNT', direction: 'DESC' },
    { key: 'nameasc', field: 'NAME', direction: 'ASC', label: 'Name A-Z' },
    { key: 'namedesc', field: 'NAME', direction: 'DESC', label: 'Name Z-A' },
    { key: 'priceasc', field: 'PRICE', direction: 'ASC', label: 'Price from low to high' },
    { key: 'pricedesc', field: 'PRICE', direction: 'DESC', label: 'Price from high to low' },
  ]
  const defaultSort = sortOptions.find(sort => sort.field === 'DEFAULT')?.key || sortOptions[0]?.key
  const { sortApply } = useDataLayer()
  const { t } = useI18n()

  function updateSort(value: string) {
    const route = useRoute()
    const router = useRouter()

    const query = {
      ...route.query,
      sort: value,
    }

    if (value === defaultSort) {
      delete query.sort
    }

    const selectedSort = sortOptions.find(sort => sort.key === value)?.label ?? ''
    if (selectedSort) {
      sortApply(t(selectedSort))
    }

    delete query.p // Reset page

    router.push({ query })
  }

  const paginatorMethods = {
    updateView,
    updateItemsPerPage,
    updateSort,
    updatePage,
  }

  const route = useRoute()

  const page = computed<number>(() => {
    return parseInt(route.query.p || defaultPage)
  })

  const itemsPerPage = computed<number>(() => {
    return parseInt(route.query.limit || defaultItemsPerPage)
  })

  const sort = computed<string>(() => {
    return route.query.sort || defaultSort
  })

  const totalPages = computed<number>(() => {
    return Math.ceil(totalCount.value / itemsPerPage.value)
  })

  return {
    page,
    defaultPage,
    updatePage,

    sort,
    sortOptions,
    defaultSort,
    updateSort,

    itemsPerPage,
    itemsPerPageOptions,
    defaultItemsPerPage,
    updateItemsPerPage,

    view,
    updateView,

    totalPages,
    totalCount,

    paginatorMethods,
  }
}
